/**
 * E53 · the portal's side of the wind rose — the field lists the data provider asks for, the shapes it gets back, and the pure helpers
 * the hub uses (the drawing's geometry, the means, the in-session drafts). The rules (a name, a behaviour for an alternative, every need
 * rated 1–10, rated before chosen, who may act with the client) are the deciders'; the hub only shapes what it sends. The portal takes
 * no dependency on `@cc/domain`: the geometry and the arithmetic below are the same as `values/rose.ts` there, and the specs check the
 * numbers the people see (5.5, 7.5).
 */
export const ROSE_NEEDS = [
  'loveAcceptance',
  'empathyUnderstanding',
  'predictabilityReliability',
  'safety',
  'competence',
  'autonomy',
  'selfExpression',
  'spontaneity',
] as const;
export type RoseNeed = (typeof ROSE_NEEDS)[number];
export type RoseScores = Record<RoseNeed, number>;
export type RoseExamples = Partial<Record<RoseNeed, string | null>>;

export const ROSE_ENOUGH = 8;
export const SCORE_MAX = 10;

export type RosePlanItem = { what: string; withWhom: string; resources: string };
export type RoseRating = {
  ratingId: string;
  ratedAt: string;
  basis: 'lived' | 'imagined';
  scores: RoseScores;
  examples: RoseExamples;
  rawMean: number;
  cappedMean: number;
  ratedInSession: boolean;
};
export type RoseStrategy = {
  strategyId: string;
  name: string;
  kind: 'current' | 'alternative';
  behaviors: string[];
  inspiredBy: { kind: string; ref: string | null } | null;
  describedAt: string;
  describedInSession: boolean;
  scores: RoseScores | null;
  examples: RoseExamples;
  basis: 'lived' | 'imagined' | null;
  ratedAt: string | null;
  rawMean: number | null;
  cappedMean: number | null;
  history: RoseRating[];
  chosen: {
    choiceId: string;
    chosenAt: string;
    value: string;
    beliefs: string[];
    sharedBy: string[];
    comparedWith: string[];
    chosenInSession: boolean;
  } | null;
  plan: {
    planId: string;
    writtenAt: string;
    planWeek: RosePlanItem[];
    planMonth: RosePlanItem[];
    planYear: RosePlanItem[];
  } | null;
};
export type RoseRow = {
  subject: string;
  ratedAt: string | null;
  chosenStrategyId: string | null;
  strategies: RoseStrategy[];
};

const SCORE_FIELDS = [...ROSE_NEEDS];
const PLAN_ITEM_FIELDS = ['what', 'withWhom', 'resources'];

export const ROSE_FIELDS = [
  'subject',
  'ratedAt',
  'chosenStrategyId',
  {
    strategies: [
      'strategyId',
      'name',
      'kind',
      'behaviors',
      { inspiredBy: ['kind', 'ref'] },
      'describedAt',
      'describedInSession',
      { scores: SCORE_FIELDS },
      { examples: SCORE_FIELDS },
      'basis',
      'ratedAt',
      'rawMean',
      'cappedMean',
      {
        history: [
          'ratingId',
          'ratedAt',
          'basis',
          { scores: SCORE_FIELDS },
          { examples: SCORE_FIELDS },
          'rawMean',
          'cappedMean',
          'ratedInSession',
        ],
      },
      {
        chosen: [
          'choiceId',
          'chosenAt',
          'value',
          'beliefs',
          'sharedBy',
          'comparedWith',
          'chosenInSession',
        ],
      },
      {
        plan: [
          'planId',
          'writtenAt',
          { planWeek: PLAN_ITEM_FIELDS },
          { planMonth: PLAN_ITEM_FIELDS },
          { planYear: PLAN_ITEM_FIELDS },
        ],
      },
    ],
  },
];

// ── the drawing ────────────────────────────────────────────────────────────────────────────────

export const ROSE_SIZE = 300;
const CENTRE = ROSE_SIZE / 2;
export const ROSE_RADIUS = 110;

/** Ray `i` points up and runs clockwise, in his order. */
export const roseAngle = (i: number): number =>
  -Math.PI / 2 + (2 * Math.PI * i) / ROSE_NEEDS.length;

export const rosePoint = (i: number, score: number, radius = ROSE_RADIUS) => ({
  x: CENTRE + ((radius * score) / SCORE_MAX) * Math.cos(roseAngle(i)),
  y: CENTRE + ((radius * score) / SCORE_MAX) * Math.sin(roseAngle(i)),
});

/** The eight raw scores as an SVG `points` string. */
export const rosePolygon = (scores: RoseScores): string =>
  ROSE_NEEDS.map((need, i) => {
    const p = rosePoint(i, scores[need]);
    return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
  }).join(' ');

export const ROSE_CENTRE = CENTRE;

/** One decimal, the way the rose prints a mean. */
export const meanText = (n: number | null | undefined): string =>
  n === null || n === undefined ? '–' : n.toFixed(1);

/** A mean read from raw scores — the same arithmetic as the server's (Σ min(score, 8) / 8). */
export const cappedMean = (scores: RoseScores): number =>
  Math.round(
    (ROSE_NEEDS.reduce((s, n) => s + Math.min(scores[n], ROSE_ENOUGH), 0) /
      ROSE_NEEDS.length) *
      10,
  ) / 10;

// ── the in-session drafts ──────────────────────────────────────────────────────────────────────

const lines = (text: string): string[] =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

export type DescribeDraft = {
  name: string;
  kind: 'current' | 'alternative';
  behaviors: string;
  inspiredKind: string | null;
  inspiredRef: string;
};
export const emptyDescribe = (): DescribeDraft => ({
  name: '',
  kind: 'current',
  behaviors: '',
  inspiredKind: null,
  inspiredRef: '',
});
export const describeOk = (d: DescribeDraft): boolean =>
  d.name.trim().length > 0 &&
  (d.kind === 'current' || lines(d.behaviors).length > 0);
export const describeInput = (clientId: string, strategyId: string, d: DescribeDraft) => ({
  subject: clientId,
  strategyId,
  name: d.name.trim(),
  kind: d.kind,
  behaviors: lines(d.behaviors).slice(0, 10),
  inspiredBy: d.inspiredKind
    ? { kind: d.inspiredKind, ref: d.inspiredRef.trim() || null }
    : null,
});

export type RateDraft = {
  strategyId: string | null;
  basis: 'lived' | 'imagined';
  scores: Partial<Record<RoseNeed, number | null>>;
  examples: Partial<Record<RoseNeed, string>>;
};
export const emptyRate = (): RateDraft => ({
  strategyId: null,
  basis: 'lived',
  scores: {},
  examples: {},
});
export const rateOk = (d: RateDraft): boolean =>
  !!d.strategyId &&
  ROSE_NEEDS.every((n) => {
    const v = d.scores[n];
    return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 10;
  });
export const rateInput = (clientId: string, d: RateDraft) => ({
  subject: clientId,
  strategyId: d.strategyId as string,
  scores: Object.fromEntries(ROSE_NEEDS.map((n) => [n, d.scores[n] as number])),
  examples: Object.fromEntries(
    ROSE_NEEDS.map((n) => [n, (d.examples[n] ?? '').trim() || null]),
  ),
  basis: d.basis,
});

export type ChooseDraft = {
  strategyId: string | null;
  value: string;
  beliefs: string;
  sharedBy: string;
};
export const emptyChoose = (): ChooseDraft => ({
  strategyId: null,
  value: '',
  beliefs: '',
  sharedBy: '',
});
export const chooseOk = (d: ChooseDraft): boolean =>
  !!d.strategyId && d.value.trim().length > 0;
export const chooseInput = (
  clientId: string,
  d: ChooseDraft,
  others: readonly string[],
) => ({
  subject: clientId,
  strategyId: d.strategyId as string,
  comparedWith: others.filter((id) => id !== d.strategyId),
  value: d.value.trim(),
  beliefs: lines(d.beliefs),
  sharedBy: lines(d.sharedBy),
});

/** A mobile-sized id for a new way (the server has no minting for a describe; the therapist's act mints its id here). */
export const newId = (): string => {
  const hex = (n: number) =>
    Array.from({ length: n }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join('');
  const time = Date.now().toString(16).padStart(12, '0');
  return `${time.slice(0, 8)}-${time.slice(8, 12)}-7${hex(3)}-${
    ['8', '9', 'a', 'b'][Math.floor(Math.random() * 4)]
  }${hex(3)}-${hex(12)}`;
};

// ── the next step (which of the three buttons is primary) ──────────────────────────────────────

export type RoseStep = 'describe' | 'rate' | 'choose' | null;

/** no ways → describe; ways but none rated → rate; rated but none chosen → choose; otherwise no step is primary. */
export const roseNextStep = (
  ways: readonly { scores: unknown; chosen: unknown }[],
): RoseStep => {
  if (ways.length === 0) return 'describe';
  const rated = ways.filter((w) => w.scores);
  if (rated.length === 0) return 'rate';
  if (!ways.some((w) => w.chosen)) return 'choose';
  return null;
};
